// Countries for the end-of-battle form (6 Oct): the United Kingdom first, then Ireland and Georgia, then every other country by
// its English name. Names come from the browser (Intl.DisplayNames), so the list needs no translations of its own.
// 16 Oct (the Tbilisi Live, where a student in Tbilisi picked the first entry, the United Kingdom): the country the phone is
// most likely in goes to the very top (countriesFor), read from its time zone (ZONES, a short curated table) or else from the
// region of the browser's language. Nothing is preselected: the form's field stays empty and required.
export const UK = 'United Kingdom';
/** 14 Oct: third in the list for the Tbilisi streams, with its own list of medical schools (data/medSchools). */
export const GEORGIA = 'Georgia';
const HEAD: ReadonlyArray<readonly [string, string]> = [['GB', UK], ['IE', 'Ireland'], ['GE', GEORGIA]];
const OTHERS = ('AF AL DZ AD AO AG AR AM AU AT AZ BS BH BD BB BY BE BZ BJ BT BO BA BW BR BN BG BF BI CV KH CM CA CF TD CL CN CO KM CG CD CR CI '
  + 'HR CU CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FJ FI FR GA GM DE GH GR GD GT GN GW GY HT HN HK HU IS IN ID IR IQ IL IT JM JP JO KZ KE KI '
  + 'KW KG LA LV LB LS LR LY LI LT LU MO MG MW MY MV ML MT MH MR MU MX FM MD MC MN ME MA MZ MM NA NR NP NL NZ NI NE NG KP MK NO OM PK PW PS PA PG '
  + 'PY PE PH PL PT PR QA RO RU RW KN LC VC WS SM ST SA SN RS SC SL SG SK SI SB SO ZA KR SS ES LK SD SR SE CH SY TW TJ TZ TH TL TG TO TT TN TR TM '
  + 'TV UG UA AE US UY UZ VU VA VE VN YE ZM ZW').split(' ');

function names(codes: string[]): string[] {
  let dn: Intl.DisplayNames | undefined;
  try { dn = new Intl.DisplayNames(['en-GB'], { type: 'region' }); } catch { dn = undefined; }
  return codes.map((c) => dn?.of(c) ?? c);
}

const OTHER_NAMES = names(OTHERS);
/** The name on the list for each country code. */
const NAME_OF = new Map<string, string>([...HEAD, ...OTHERS.map((c, i) => [c, OTHER_NAMES[i]] as const)]);

export const COUNTRIES: readonly string[] = [...HEAD.map(([, n]) => n), ...[...OTHER_NAMES].sort((a, b) => a.localeCompare(b, 'en-GB'))];

/**
 * Time zones (IANA names, with the older spellings some browsers still report) and the country each one means: the UK,
 * Ireland and Georgia, the countries our players most often come from, North America, Australia and New Zealand, and the
 * European Union. Anything else falls back to the browser's language.
 */
export const ZONES: Readonly<Record<string, string>> = Object.fromEntries(`
  GB Europe/London Europe/Belfast
  IE Europe/Dublin
  GE Asia/Tbilisi
  IN Asia/Kolkata Asia/Calcutta
  PK Asia/Karachi
  BD Asia/Dhaka Asia/Dacca
  NG Africa/Lagos
  EG Africa/Cairo
  SA Asia/Riyadh
  AE Asia/Dubai
  QA Asia/Qatar
  KW Asia/Kuwait
  OM Asia/Muscat
  JO Asia/Amman
  MY Asia/Kuala_Lumpur Asia/Kuching
  SG Asia/Singapore
  LK Asia/Colombo
  NP Asia/Kathmandu Asia/Katmandu
  KE Africa/Nairobi
  GH Africa/Accra
  ZA Africa/Johannesburg
  US America/New_York America/Detroit America/Indiana/Indianapolis America/Indianapolis America/Kentucky/Louisville America/Louisville America/Chicago America/Denver America/Boise America/Phoenix America/Los_Angeles America/Anchorage America/Juneau Pacific/Honolulu
  CA America/Toronto America/Montreal America/Vancouver America/Edmonton America/Winnipeg America/Regina America/Halifax America/Moncton America/St_Johns
  AU Australia/Sydney Australia/Melbourne Australia/Brisbane Australia/Perth Australia/Adelaide Australia/Hobart Australia/Darwin Australia/Canberra
  NZ Pacific/Auckland
  AT Europe/Vienna
  BE Europe/Brussels
  BG Europe/Sofia
  HR Europe/Zagreb
  CY Asia/Nicosia Europe/Nicosia
  CZ Europe/Prague
  DK Europe/Copenhagen
  EE Europe/Tallinn
  FI Europe/Helsinki
  FR Europe/Paris
  DE Europe/Berlin
  GR Europe/Athens
  HU Europe/Budapest
  IT Europe/Rome
  LV Europe/Riga
  LT Europe/Vilnius
  LU Europe/Luxembourg
  MT Europe/Malta
  NL Europe/Amsterdam
  PL Europe/Warsaw
  PT Europe/Lisbon Atlantic/Madeira Atlantic/Azores
  RO Europe/Bucharest
  SK Europe/Bratislava
  SI Europe/Ljubljana
  ES Europe/Madrid Atlantic/Canary
  SE Europe/Stockholm
`.trim().split('\n').flatMap((line) => { const [code, ...zones] = line.trim().split(/\s+/); return zones.map((z) => [z, code]); }));

/** The region of a language tag ('en-GB', 'zh-Hant-TW', 'en_IN'), when it names a country: two letters after the language. */
const regionOf = (tag: string): string | undefined => tag.split(/[-_]/).slice(1).find((part) => /^[A-Za-z]{2}$/.test(part))?.toUpperCase();

/** Pure: the country on the list a time zone, or else one of the browser's languages, points to; undefined when neither does. */
export function likelyCountry(timeZone: string | undefined, languages: readonly string[]): string | undefined {
  const byZone = timeZone ? ZONES[timeZone] : undefined;
  if (byZone) return NAME_OF.get(byZone);
  for (const tag of languages) {
    const region = typeof tag === 'string' ? regionOf(tag) : undefined;
    const name = region ? NAME_OF.get(region) : undefined;
    if (name) return name;
  }
  return undefined;
}

/** This phone's likely country, from its time zone and then its languages. */
export function phoneCountry(): string | undefined {
  let zone: string | undefined;
  try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { zone = undefined; }
  let languages: readonly string[] = [];
  try {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    languages = nav?.languages?.length ? nav.languages : nav?.language ? [nav.language] : [];
  } catch { languages = []; }
  return likelyCountry(zone, languages);
}

/** The list with `first` at the very top (once) and the rest in today's order; today's order when it is unknown. */
export function countriesFor(first: string | undefined): readonly string[] {
  if (!first || !COUNTRIES.includes(first) || COUNTRIES[0] === first) return COUNTRIES;
  return [first, ...COUNTRIES.filter((c) => c !== first)];
}
